import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Mail, Lock, User, UserPlus } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useUserAuth } from '../../contexts/UserAuthContext';
import { Navbar } from '../Navbar';
import { Footer } from '../Footer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function UserRegister() {
  const { t } = useLanguage();
  const { login } = useUserAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: '', display_name: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const { data } = await axios.post(`${API}/user/register`, form);
      login(data.token, data.user);
      navigate('/account');
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(Array.isArray(detail) ? detail.map(d => d.msg).join(' ') : (detail || 'Fehler'));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#020817] flex flex-col">
      <Navbar />
      <main className="flex-1 flex items-center justify-center py-20 px-4">
        <div className="w-full max-w-sm">
          <div className="bg-[#0f172a] border border-slate-800 rounded-2xl p-8">
            <div className="text-center mb-8">
              <div className="w-12 h-12 bg-cyan-500/10 border border-cyan-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <UserPlus className="w-6 h-6 text-cyan-400" />
              </div>
              <h1 className="text-xl font-bold text-white">{t.user.registerTitle}</h1>
            </div>

            {error && (
              <div data-testid="register-error"
                className="mb-4 px-4 py-3 bg-red-500/10 border border-red-500/20 rounded-xl text-sm text-red-400">
                {error}
              </div>
            )}

            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="text-xs font-medium text-slate-400 uppercase tracking-wider block mb-1">
                  {t.user.displayName}
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <input type="text" required value={form.display_name}
                    onChange={e => setForm(f => ({ ...f, display_name: e.target.value }))}
                    data-testid="user-display-name"
                    className="w-full pl-10 pr-3 py-2.5 bg-slate-800 border border-slate-700 rounded-xl text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-cyan-500 transition-colors"
                    placeholder={t.user.displayNamePlaceholder} />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-400 uppercase tracking-wider block mb-1">
                  {t.user.email}
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <input type="email" required value={form.email}
                    onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                    data-testid="user-email"
                    className="w-full pl-10 pr-3 py-2.5 bg-slate-800 border border-slate-700 rounded-xl text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-cyan-500 transition-colors"
                    placeholder="dein@email.de" />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-400 uppercase tracking-wider block mb-1">
                  {t.user.password}
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <input type="password" required minLength={8} value={form.password}
                    onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                    data-testid="user-password"
                    className="w-full pl-10 pr-3 py-2.5 bg-slate-800 border border-slate-700 rounded-xl text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-cyan-500 transition-colors"
                    placeholder="Min. 8 Zeichen" />
                </div>
              </div>
              <button type="submit" disabled={loading} data-testid="user-register-btn"
                className="w-full py-2.5 bg-cyan-500 text-slate-950 font-semibold rounded-xl hover:bg-cyan-400 transition-colors disabled:opacity-50 flex items-center justify-center gap-2 text-sm">
                {loading ? <><Loader2 className="w-4 h-4 animate-spin" />{t.user.registering}</> : t.user.registerBtn}
              </button>
            </form>

            <p className="text-center text-xs text-slate-500 mt-5">
              {t.user.hasAccount}{' '}
              <Link to="/login" className="text-cyan-400 hover:text-cyan-300 font-medium">
                {t.user.loginBtn}
              </Link>
            </p>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
